import { useField, useFormikContext } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Skeleton } from '@components';
import { Direction, DisplayMode, InputMod, VariantType } from '@enums';
import { cn, validateValueEqual } from '@utils';

import ErrorMessage from './errorMessage';
import { InputWithIcons } from './inputWithIcons';

export interface InputTextProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  name: string;
  errorClassName?: string;
  isCorrect?: boolean;
  hints?: string;
  fontSize?: string;
  adornment?: React.ReactNode;
  transform?: (event: React.ChangeEvent<HTMLInputElement>) => React.ChangeEvent<HTMLInputElement>;
  variant?: VariantType;
  clearFunction?: () => void;
  inputMod?: InputMod;
  withoutClear?: boolean;
  label?: string;
  autoComplete?: string;
  isError?: boolean;
  adornmentPosition?: Direction;
  loading?: boolean;
  displayMode?: DisplayMode;
  withoutErrorMessage?: boolean;
  adornmentClassName?: string;
}

export const BaseTextInput: React.FC<InputTextProps> = ({
  name,
  errorClassName = '',
  isCorrect,
  isError = false,
  hints,
  type = 'text',
  onBlur,
  transform,
  adornment,
  onPaste,
  disabled,
  autoComplete = 'off',
  className = `py-1.5 ${type === 'number' ? 'pr-16' : 'pr-10'}`,
  inputMod,
  clearFunction,
  maxLength,
  min,
  max,
  required = false,
  withoutClear,
  adornmentPosition = Direction.Left,
  value,
  loading = false,
  onChange,
  label,
  placeholder,
  withoutErrorMessage = false,
  onWheel,
  adornmentClassName = '',
}) => {
  const [field, meta, helpers] = useField(name);
  const formik = useFormikContext();

  const [inputState, setInputState] = useState({
    showClearIcon: false,
    isHide: 'password',
    userHasInteracted: false,
  });
  const [padding, setPadding] = useState({ right: '10px', left: '0px', iconRight: '12px' });

  const adornmentRef = useRef<HTMLDivElement>(null);
  const iconsContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousPaddingRef = useRef({
    right: '',
    left: '',
    iconRight: '',
  });
  const stateTransitionRef = useRef(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const isFilled = validateValueEqual(inputMod, InputMod.Filled);
  const isPassword = validateValueEqual(type, 'password');
  const isNumber = validateValueEqual(type, 'number');
  const isAdornmentLeft = validateValueEqual(adornmentPosition, Direction.Left);
  const isAdornmentRight = validateValueEqual(adornmentPosition, Direction.Right);
  const isAdornmentOutside = validateValueEqual(adornmentPosition, Direction.Outside);

  const isFieldError = useMemo(
    () => Boolean(meta.error && (meta.initialTouched || meta.touched || formik.submitCount > 0)),
    [meta.error, meta.initialTouched, meta.touched, formik.submitCount],
  );

  useEffect(() => {
    const calculatePadding = (
      adornmentWidth: number,
      iconsWidth: number,
    ): { right: string; left: string; iconRight: string } => ({
      right: `${(isAdornmentLeft ? adornmentWidth : 0) + iconsWidth + 3}px`,
      left: `${!isAdornmentLeft ? adornmentWidth + 10 : isFilled ? (adornment ? 12 : 16) : 12}px`,
      iconRight: `${(isAdornmentLeft ? adornmentWidth : 0) + (isFilled ? 14 : meta.touched ? 8 : 10)}px`,
    });

    const updatePadding = () => {
      const adornmentWidth = adornmentRef.current?.offsetWidth ?? 0;
      const iconsContainerWidth = iconsContainerRef.current?.offsetWidth ?? 0;

      const newPadding = calculatePadding(adornmentWidth, iconsContainerWidth);

      if (
        newPadding.right !== previousPaddingRef.current.right ||
        newPadding.left !== previousPaddingRef.current.left ||
        newPadding.iconRight !== previousPaddingRef.current.iconRight
      ) {
        previousPaddingRef.current = newPadding;
        setPadding(newPadding);
      }
    };

    const debouncedUpdate = debounce(() => {
      requestAnimationFrame(updatePadding);
    }, 100);

    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    resizeObserverRef.current = new ResizeObserver(debouncedUpdate);

    if (iconsContainerRef.current) {
      resizeObserverRef.current.observe(iconsContainerRef.current);
    }
    if (adornmentRef.current) {
      resizeObserverRef.current.observe(adornmentRef.current);
    }

    updatePadding();

    return () => {
      debouncedUpdate.cancel();
      resizeObserverRef.current?.disconnect();
    };
  }, [isAdornmentLeft, isFilled, adornment, meta.touched]);

  const handleClear = useCallback(() => {
    helpers.setValue('');
    clearFunction?.();
  }, [helpers, clearFunction]);

  const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    if (stateTransitionRef.current) return;

    stateTransitionRef.current = true;
    requestAnimationFrame(() => {
      setInputState((prev) => {
        if (!prev.userHasInteracted || !prev.showClearIcon) {
          return { ...prev, showClearIcon: true, userHasInteracted: true };
        }
        return prev;
      });
      stateTransitionRef.current = false;
    });

    event.target.focus();
  }, []);

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (stateTransitionRef.current) return;

      stateTransitionRef.current = true;

      const processValue = (value: string): string | number => {
        const processedValue: string | number = value?.trim?.() ?? value;

        if (isNumber && min !== undefined) {
          const numValue = Number(processedValue);
          if (isNaN(numValue) || numValue < Number(min)) {
            return Number(min);
          }
          return numValue;
        }

        return processedValue;
      };

      const trimmedValue = processValue(event.target.value);

      requestAnimationFrame(() => {
        setInputState((prev) => ({
          ...prev,
          showClearIcon: false,
          userHasInteracted: false,
        }));

        const syntheticEvent: NumberInputEvent = {
          target: {
            name: field.name,
            value: trimmedValue,
          },
        };

        field.onChange(syntheticEvent);
        field.onBlur(event);
        onBlur?.(event);

        if (event.target && event.target instanceof HTMLInputElement) {
          event.target.value = trimmedValue.toString();
        }

        stateTransitionRef.current = false;
      });
    },
    [field.name, field.onChange, field.onBlur, isNumber, min, onBlur],
  );

  const handleHideIcon = useCallback(() => {
    setInputState((prev) => ({
      ...prev,
      isHide: prev.isHide === 'password' ? ('text' as const) : ('password' as const),
    }));
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const transformedEvent = transform ? transform(event) : event;
      field.onChange(transformedEvent);
      onChange?.(transformedEvent);
    },
    [field, transform, onChange],
  );

  type NumberInputEvent = {
    target: {
      name: string;
      value: string | number;
    };
  };

  useEffect(() => {
    return () => {
      stateTransitionRef.current = false;
    };
  }, []);

  if (loading) {
    return <Skeleton className="h-10 rounded-md w-full" />;
  }

  return (
    <motion.div
      className="flex flex-col h-full space-y-3 w-full"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex flex-1 gap-3">
        <motion.div className="relative group flex flex-1 items-center">
          {adornment && isAdornmentRight && (
            <div ref={adornmentRef} className={cn('absolute left-0 h-full flex', adornmentClassName)}>
              {adornment}
            </div>
          )}
          <motion.input
            {...field}
            name={name}
            ref={inputRef}
            placeholder={placeholder}
            value={value ?? field.value ?? ''}
            autoComplete={autoComplete}
            required={required}
            onFocus={handleFocus}
            maxLength={maxLength}
            onBlur={handleBlur}
            onChange={handleChange}
            onPaste={onPaste}
            disabled={disabled}
            type={isPassword ? inputState.isHide : type}
            min={isNumber ? min : undefined}
            max={isNumber ? max : undefined}
            aria-invalid={!!(meta.error && meta.touched)}
            aria-describedby={isFieldError ? `${name}-error` : undefined}
            className={cn(
              'block rounded-md placeholder-gray-400 bg-gray-50/10',
              'w-full px-2.5 py-1.5 text-us text-ellipsis hover:cursor-text sm:leading-6',
              'border-0 ring-1 ring-theme-border-default',
              (isFieldError || (isError && withoutErrorMessage)) &&
                'ring-theme-error-main bg-red-500/10 focus:ring-theme-error-main',
              className,
            )}
            style={{ paddingRight: padding.right, paddingLeft: padding.left }}
            animate={{ scale: inputState.userHasInteracted && !adornment ? 1.02 : 1 }}
            onWheel={onWheel}
            transition={{ duration: 0.15 }}
          />
          <AnimatePresence>
            {isFilled && !!label && (
              <motion.label
                htmlFor={name}
                transition={{ duration: 0.2 }}
                animate={{ x: inputState.userHasInteracted && !adornment ? -1.05 : 0 }}
                className={cn(
                  'flex absolute top-0 left-0 h-full items-center text-gray-400 font-medium',
                  'transform transition-all pointer-events-none select-none',
                  'group-focus-within:h-4/6',
                  field.value
                    ? 'text-smLabel peer-valid:h-4/6 peer-invalid:h-4/6 peer-disabled:h-4/6'
                    : 'text-xs group-focus-within:text-smLabel',
                  adornment ? 'pl-3' : 'pl-4',
                )}
              >
                {label}
              </motion.label>
            )}
          </AnimatePresence>
          <InputWithIcons
            isError={isFieldError}
            isCorrect={isCorrect}
            field={field}
            iconInputRef={iconsContainerRef}
            withoutClear={withoutClear}
            hints={hints}
            isNumber={isNumber}
            paddingRight={padding.iconRight}
            clearFunction={clearFunction}
            isPassword={isPassword}
            isHide={inputState.isHide}
            handleHideIcon={handleHideIcon}
            handleClear={handleClear}
            showClearIcon={inputState.showClearIcon}
          />
          {adornment && isAdornmentLeft && (
            <div ref={adornmentRef} className={cn('absolute right-0 h-full flex', adornmentClassName)}>
              {adornment}
            </div>
          )}
        </motion.div>
        {adornment && isAdornmentOutside && <div className={cn('h-full flex', adornmentClassName)}>{adornment}</div>}
      </div>
      <AnimatePresence>
        {!withoutErrorMessage && isFieldError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ErrorMessage isError={isFieldError} errorClassName={errorClassName} meta={meta} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
